import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';

import { FieldDataSourceModel, TableFieldValueType } from '@models/module/fields/main';
import { FieldDefinitionService } from '@module-instance/services';
import { FieldsService } from '@shared/components/fields/common';
import { Observable } from 'rxjs';
import { TableFieldConfig } from '../common';

type TableFieldParams = {
  entityInstanceId: number;
  id: number;
  dataSourceId: number;
  viewMode?: boolean;
  /** required to populate the options of cipo-list controls */
  value?: TableFieldValueType[];
  /** required to populate the options of cipo-list controls */
  oldValue?: TableFieldValueType[];
  isDraft?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class TableFieldService {
  private readonly fieldService = inject(FieldsService);
  private readonly fieldDefinitionService = inject(FieldDefinitionService);

  getTableFields({
    entityInstanceId,
    oldValue,
    viewMode,
    value,
    id,
    isDraft,
  }: Pick<TableFieldParams, 'oldValue' | 'value' | 'id' | 'entityInstanceId' | 'viewMode' | 'isDraft'>): Observable<
    TableFieldConfig[]
  > {
    return this.fieldDefinitionService.getTableFields(entityInstanceId, id).pipe(
      map(fields => {
        return fields.map(field => {
          const { id, dataSourceId, aggregateId, width, name } = field;
          const { fieldOptions } = this.fieldService.mapFormFieldModel(field, isDraft);
          /* Data source is populated only in view mode */
          fieldOptions.options = viewMode ? this.getFieldDataSource({ id, dataSourceId, oldValue, value }) : undefined;
          fieldOptions.preventAllSelectedLabel = true;
          return {
            key: id.toString(),
            name,
            width,
            aggregateId,
            value: field.value,
            ...fieldOptions,
          } as TableFieldConfig;
        });
      }),
    );
  }

  private getFieldDataSource({
    id,
    dataSourceId,
    oldValue,
    value,
  }: Pick<TableFieldParams, 'id' | 'dataSourceId' | 'oldValue' | 'value'>) {
    if (!dataSourceId || (!value?.length && !oldValue?.length)) {
      return null;
    }
    const dataSources = [...(oldValue ?? []), ...(value ?? [])]
      .map(row => row[`${id}_dataSource`] as unknown as FieldDataSourceModel[])
      .filter(arr => !!arr?.length)
      .flat();

    // remove duplicates
    const seen = new Set();
    const uniqueDataSources = dataSources.filter(item => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });

    return uniqueDataSources.length > 0 ? uniqueDataSources : undefined;
  }
}
